/**
 * Browser UI entry point — ERA Open Railway Maps query tool.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { parse } from '../parser/index.js';
import { ParseError } from '../ast/index.js';
import { plan } from '../planner/index.js';
import { registryRules, unmappedRules } from '../registry/index.js';
import { fetchOverpass, OverpassError } from '../adapters/overpass/index.js';
import { fetchFacility, fetchMilestone, OrmApiError } from '../adapters/orm-api/index.js';
import { toFeatureCollection } from '../transform/geojson/index.js';
import { attachProvenance, provenanceSummary } from '../transform/provenance/index.js';
import { overpassCache, ormCache, cacheKey } from '../cache/index.js';
import type { ExecutionPlan } from '../planner/index.js';

// ---------------------------------------------------------------------------
// Example queries
// ---------------------------------------------------------------------------

interface ExampleQuery {
  label: string;
  eraProfile: string;
  sparql: string;
}

const EXAMPLES: ExampleQuery[] = [
  {
    label: '\u{1F689} Stations near Karlsruhe',
    eraProfile: `ERA Entity  : era:OperationalPoint
ERA Property: era:opName
Spatial     : FILTER(?near = "49.0,8.4,5000")
              \u2192 Overpass (around:5000,49.0,8.4)
Mapping     : node["railway"="station"]
              node["railway"="halt"]
Confidence  : HIGH (direct tag mapping)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
SELECT ?op ?name WHERE {
  ?op a era:OperationalPoint ;
      era:opName ?name .
  FILTER(?near = "49.0,8.4,5000")
}
LIMIT 50`,
  },
  {
    label: '\u26A1 15 kV / 16.7 Hz tracks',
    eraProfile: `ERA Entity  : era:RunningTrack
ERA Property: era:electrificationSystem (era:ContactLineSystem)
              era:voltage       (15000)
              era:frequency     (16.7)
Spatial     : FILTER(?near = "48.7,9.2,8000")
              \u2192 Overpass (around:8000,48.7,9.2)
Mapping     : way["railway"="rail"]
                 ["electrified"="contact_line"]
                 ["voltage"="15000"]
                 ["frequency"="16.7"]
Confidence  : HIGH (direct tag mapping)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
PREFIX xsd: <http://www.w3.org/2001/XMLSchema#>
SELECT ?track WHERE {
  ?track a era:RunningTrack ;
         era:electrificationSystem era:ContactLineSystem ;
         era:voltage "15000"^^xsd:integer ;
         era:frequency "16.7" .
  FILTER(?near = "48.7,9.2,8000")
}
LIMIT 50`,
  },
  {
    label: '\u{1F6E4}\uFE0F Running tracks (bbox)',
    eraProfile: `ERA Entity  : era:RunningTrack
Spatial     : FILTER(?bbox = "48.9,8.3,49.1,8.6")
              \u2192 Overpass [bbox:48.9,8.3,49.1,8.6]
Mapping     : way["railway"="rail"]
              way["railway"="narrow_gauge"]
Confidence  : HIGH (direct tag mapping)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
SELECT ?track WHERE {
  ?track a era:RunningTrack .
  FILTER(?bbox = "48.9,8.3,49.1,8.6")
}
LIMIT 100`,
  },
  {
    label: '\u{1F3D7}\uFE0F Tunnels near Karlsruhe',
    eraProfile: `ERA Entity  : era:Tunnel
Spatial     : FILTER(?near = "49.0,8.4,10000")
              \u2192 Overpass (around:10000,49.0,8.4)
Mapping     : way["tunnel"="yes"]["railway"~"rail|narrow_gauge"]
              relation["tunnel"="yes"]["railway"~"rail|narrow_gauge"]
Confidence  : MEDIUM (tunnel tag usage varies)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
SELECT ?tunnel WHERE {
  ?tunnel a era:Tunnel .
  FILTER(?near = "49.0,8.4,10000")
}
LIMIT 50`,
  },
  {
    label: '\u{1F68F} Platform edges near Hbf',
    eraProfile: `ERA Entity  : era:PlatformEdge
Spatial     : FILTER(?near = "49.0093,8.4042,1000")
              \u2192 Overpass (around:1000,49.0093,8.4042)
Mapping     : way["railway"="platform"]
              node["railway"="platform"]
Confidence  : HIGH (direct tag mapping)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
SELECT ?edge WHERE {
  ?edge a era:PlatformEdge .
  FILTER(?near = "49.0093,8.4042,1000")
}
LIMIT 50`,
  },
  {
    label: '\u26A0\uFE0F Partial: ETCS level tracks',
    eraProfile: `ERA Entity  : era:RunningTrack
ERA Property: era:etcsLevel (value: 2)
              \u26A0 era:etcsLevel has NO direct OSM/ORM equivalent
Status      : PARTIAL TRANSLATION
              \u2192 Best-effort: queries rail tracks in area;
                ETCS level filtering via provenance notes only
Spatial     : FILTER(?near = "48.7,9.2,5000")
Confidence  : LOW (ETCS data absent from OSM)`,
    sparql: `PREFIX era: <http://data.europa.eu/949/>
SELECT ?track WHERE {
  ?track a era:RunningTrack ;
         era:etcsLevel 2 .
  FILTER(?near = "48.7,9.2,5000")
}
LIMIT 30`,
  },
];

// ---------------------------------------------------------------------------
// DOM references
// ---------------------------------------------------------------------------

const queryTextarea = document.getElementById('query') as HTMLTextAreaElement;
const runBtn = document.getElementById('run-btn') as HTMLButtonElement;
const statusBar = document.getElementById('status-bar') as HTMLDivElement;
const resultJson = document.getElementById('result-json') as HTMLPreElement;
const examplesGrid = document.getElementById('examples-grid') as HTMLDivElement;
const eraProfileSection = document.getElementById('era-profile-section') as HTMLDivElement;
const eraProfileBox = document.getElementById('era-profile-box') as HTMLPreElement;
const qlSection = document.getElementById('ql-section') as HTMLDivElement;
const translatedQl = document.getElementById('translated-ql') as HTMLPreElement;
const qlCopyBtn = document.getElementById('ql-copy-btn') as HTMLButtonElement;
const showOntologyBtn = document.getElementById('show-ontology-btn') as HTMLButtonElement;
const ontologyModal = document.getElementById('ontology-modal') as HTMLDialogElement;
const closeOntologyBtn = document.getElementById('close-ontology-btn') as HTMLButtonElement;
const ontologyIframe = document.getElementById('ontology-iframe') as HTMLIFrameElement;
const ontologyFallback = document.getElementById('ontology-fallback') as HTMLDivElement;
const openWebvowlBtn = document.getElementById('open-webvowl-btn') as HTMLButtonElement;

// ---------------------------------------------------------------------------
// Leaflet map setup
// ---------------------------------------------------------------------------

const map = L.map('map').setView([51.0, 10.0], 5);

// Define base layers with different visual styles
const baseLayers = {
  'OSM Standard': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }),
  'OSM Grayscale': L.tileLayer('https://tiles.wmflabs.org/bw-mapnik/{z}/{x}/{y}.png', {
    maxZoom: 18,
    attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }),
  'CartoDB Light': L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    maxZoom: 20,
    attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, \u00A9 <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
  }),
  'No Base Layer': L.tileLayer('', {
    attribution: '',
  }),
};

// Add default base layer (CartoDB Light for better contrast with colored features)
baseLayers['CartoDB Light'].addTo(map);

// Add layer control to top-right corner
L.control.layers(baseLayers, {}, { position: 'topright' }).addTo(map);

let geojsonLayer: L.GeoJSON | null = null;

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function setStatus(msg: string, kind: 'idle' | 'loading' | 'error' | 'ok' = 'idle'): void {
  statusBar.textContent = msg;
  statusBar.className = `status-${kind}`;
}

function setResult(text: string): void {
  resultJson.textContent = text;
}

// ---------------------------------------------------------------------------
// Overpass QL display helpers
// ---------------------------------------------------------------------------

function extractOverpassQL(p: ExecutionPlan): string | null {
  if (p.kind === 'overpass-primary' || p.kind === 'overpass-plus-postfilter') {
    return p.overpassQL;
  }
  if (p.kind === 'partial-translation' && p.basePlan?.kind === 'overpass-primary') {
    return p.basePlan.overpassQL;
  }
  return null;
}

function showOverpassQL(ql: string | null): void {
  if (!ql) {
    qlSection.classList.remove('visible');
    return;
  }
  translatedQl.textContent = ql;
  qlSection.classList.add('visible');
}

function hideOverpassQL(): void {
  qlSection.classList.remove('visible');
}

// ---------------------------------------------------------------------------
// Run query
// ---------------------------------------------------------------------------

async function runQuery(): Promise<void> {
  const queryText = queryTextarea.value.trim();
  if (!queryText) {
    setStatus('Please enter a query.', 'error');
    return;
  }

  setStatus('Parsing\u2026', 'loading');
  runBtn.disabled = true;
  hideOverpassQL();

  if (geojsonLayer) {
    map.removeLayer(geojsonLayer);
    geojsonLayer = null;
  }
  setResult('');

  try {
    const ast = parse(queryText);
    setStatus('Planning\u2026', 'loading');

    const executionPlan = plan(ast, registryRules, unmappedRules);

    if (executionPlan.kind === 'unsupported') {
      setStatus(`Unsupported query: ${executionPlan.reason}`, 'error');
      return;
    }

    showOverpassQL(extractOverpassQL(executionPlan));

    if (executionPlan.kind === 'partial-translation') {
      setStatus(
        'Partial translation \u2014 some ERA fields not mappable to OSM (continuing with best-effort result).',
        'idle',
      );
    } else {
      setStatus('Fetching\u2026', 'loading');
    }

    const rawResult = await fetchWithCache(executionPlan);
    const fc = toFeatureCollection(rawResult as Parameters<typeof toFeatureCollection>[0], executionPlan);
    attachProvenance(fc, executionPlan);
    renderResults(fc, executionPlan);
  } catch (err) {
    if (err instanceof ParseError) {
      setStatus(`Parse error (${err.kind}): ${err.message}`, 'error');
    } else if (err instanceof OverpassError) {
      setStatus(`Overpass API error: ${err.message}`, 'error');
    } else if (err instanceof OrmApiError) {
      setStatus(`ORM API error: ${err.message}`, 'error');
    } else {
      setStatus(`Error: ${(err as Error).message}`, 'error');
    }
    console.error(err);
  } finally {
    runBtn.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// Fetch with session cache
// ---------------------------------------------------------------------------

async function fetchWithCache(executionPlan: ExecutionPlan): Promise<unknown> {
  switch (executionPlan.kind) {
    case 'overpass-primary':
    case 'overpass-plus-postfilter': {
      const ql = executionPlan.overpassQL;
      const key = cacheKey(ql);
      if (overpassCache.has(key)) return overpassCache.get(key);
      const result = await fetchOverpass(ql);
      overpassCache.set(key, result);
      return result;
    }
    case 'orm-facility-lookup': {
      const { searchTerm, searchType, limit } = executionPlan;
      const key = cacheKey(`facility:${searchTerm}:${searchType}:${limit}`);
      if (ormCache.has(key)) return ormCache.get(key);
      const result = await fetchFacility(searchTerm, searchType, limit);
      ormCache.set(key, result);
      return result;
    }
    case 'orm-milestone-lookup': {
      const { lineRef, km } = executionPlan;
      const key = cacheKey(`milestone:${lineRef}:${km ?? ''}`);
      if (ormCache.has(key)) return ormCache.get(key);
      const result = await fetchMilestone(lineRef, km);
      ormCache.set(key, result);
      return result;
    }
    case 'partial-translation': {
      const base = executionPlan.basePlan;
      if (base?.kind === 'overpass-primary') {
        const key = cacheKey(base.overpassQL);
        if (overpassCache.has(key)) return overpassCache.get(key);
        const result = await fetchOverpass(base.overpassQL);
        overpassCache.set(key, result);
        return result;
      }
      if (base?.kind === 'orm-facility-lookup') {
        const key = cacheKey(`facility:${base.searchTerm}:${base.searchType}:${base.limit}`);
        if (ormCache.has(key)) return ormCache.get(key);
        const result = await fetchFacility(base.searchTerm, base.searchType, base.limit);
        ormCache.set(key, result);
        return result;
      }
      return { elements: [] };
    }
    default:
      return { elements: [] };
  }
}

// ---------------------------------------------------------------------------
// Render results
// ---------------------------------------------------------------------------

function renderResults(
  fc: ReturnType<typeof toFeatureCollection>,
  executionPlan: ExecutionPlan,
): void {
  const count = fc.features.length;

  if (count === 0) {
    setStatus('No results.', 'idle');
    setResult(JSON.stringify(fc, null, 2));
    return;
  }

  geojsonLayer = L.geoJSON(fc as GeoJSON.FeatureCollection, {
    pointToLayer: (_feature, latlng) =>
      L.circleMarker(latlng, {
        radius: 6,
        fillColor: confidenceColour(_feature.properties?.mapping_confidence ?? 0),
        color: '#333',
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.75,
      }),
    style: (feature) => ({
      color: confidenceColour(feature?.properties?.mapping_confidence ?? 0),
      weight: 2,
      opacity: 0.8,
    }),
    onEachFeature: (feature, layer) => {
      const p = feature.properties ?? {};
      const popupLines: string[] = [
        `<strong>${p['name'] ?? p['era_entity'] ?? 'Feature'}</strong>`,
        `<em>Source:</em> ${p['source_system'] ?? '\u2014'}`,
        `<em>Mapping:</em> ${p['mapping_status'] ?? '\u2014'} (${((p['mapping_confidence'] ?? 0) * 100).toFixed(0)}%)`,
        `<em>Matched by:</em> ${p['matched_by'] ?? '\u2014'}`,
      ];
      if ((p['translation_notes'] as string[] | undefined)?.length) {
        popupLines.push(`<em>Notes:</em> ${(p['translation_notes'] as string[]).join('; ')}`);
      }
      layer.bindPopup(popupLines.join('<br>'));
    },
  }).addTo(map);

  const bounds = geojsonLayer.getBounds();
  if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30] });

  const summary = provenanceSummary(fc);
  const planNote =
    executionPlan.kind === 'partial-translation'
      ? `\n\n\u26A0 Partial translation \u2014 unmapped fields: ${executionPlan.unmappedFields.join(', ')}`
      : '';

  setStatus(
    `${count} feature${count !== 1 ? 's' : ''} \u2014 confidence avg ${(summary.avgConfidence * 100).toFixed(0)}%${planNote}`,
    'ok',
  );
  setResult(JSON.stringify(summary, null, 2));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function confidenceColour(confidence: number): string {
  if (confidence >= 0.8) return '#16a34a';
  if (confidence >= 0.6) return '#ca8a04';
  return '#dc2626';
}

// ---------------------------------------------------------------------------
// Example buttons
// ---------------------------------------------------------------------------

let activeExampleBtn: HTMLButtonElement | null = null;

function buildExampleButtons(): void {
  EXAMPLES.forEach((ex, i) => {
    const btn = document.createElement('button');
    btn.className = 'ex-btn';
    btn.textContent = ex.label;
    btn.dataset['idx'] = String(i);

    btn.addEventListener('click', () => {
      queryTextarea.value = ex.sparql;

      eraProfileBox.textContent = ex.eraProfile;
      eraProfileSection.classList.add('visible');

      activeExampleBtn?.classList.remove('active');
      btn.classList.add('active');
      activeExampleBtn = btn;

      hideOverpassQL();
      setResult('// Press \u25B6 Run Query to execute');
      setStatus(`Example loaded \u2014 press \u25B6 Run Query or Ctrl+Enter`, 'idle');
    });

    examplesGrid.appendChild(btn);
  });
}

queryTextarea.addEventListener('input', () => {
  activeExampleBtn?.classList.remove('active');
  activeExampleBtn = null;
  eraProfileSection.classList.remove('visible');
});

// ---------------------------------------------------------------------------
// Copy QL button
// ---------------------------------------------------------------------------

qlCopyBtn.addEventListener('click', () => {
  const ql = translatedQl.textContent ?? '';
  void navigator.clipboard.writeText(ql).then(() => {
    const orig = qlCopyBtn.textContent;
    qlCopyBtn.textContent = 'Copied \u2713';
    setTimeout(() => { qlCopyBtn.textContent = orig; }, 1500);
  });
});

// ---------------------------------------------------------------------------
// Ontology graph modal
// ---------------------------------------------------------------------------

const WEBVOWL_URL = 'https://rinf.data.era.europa.eu/era-vocabulary/webvowl/index.html';

showOntologyBtn.addEventListener('click', () => {
  ontologyIframe.src = WEBVOWL_URL;
  ontologyFallback.classList.remove('visible');
  ontologyModal.showModal();
});

closeOntologyBtn.addEventListener('click', () => {
  ontologyModal.close();
});

// Close modal when clicking backdrop
ontologyModal.addEventListener('click', (e) => {
  if (e.target === ontologyModal) {
    ontologyModal.close();
  }
});

// Handle iframe load errors
ontologyIframe.addEventListener('error', () => {
  ontologyFallback.classList.add('visible');
});

// Fallback button opens WebVOWL in new tab
openWebvowlBtn.addEventListener('click', () => {
  window.open(WEBVOWL_URL, '_blank', 'noopener,noreferrer');
});

// Set a timeout to show fallback if iframe doesn't load quickly
let iframeLoadTimeout: number | undefined;
showOntologyBtn.addEventListener('click', () => {
  clearTimeout(iframeLoadTimeout);
  iframeLoadTimeout = window.setTimeout(() => {
    // If iframe hasn't fired load event after 10 seconds, assume it failed
    if (ontologyIframe.src && !ontologyIframe.contentWindow?.document.body) {
      ontologyFallback.classList.add('visible');
    }
  }, 10000);
});

ontologyIframe.addEventListener('load', () => {
  clearTimeout(iframeLoadTimeout);
});

// ---------------------------------------------------------------------------
// Initialise
// ---------------------------------------------------------------------------

runBtn.addEventListener('click', () => void runQuery());
queryTextarea.addEventListener('keydown', (e) => {
  if (e.ctrlKey && e.key === 'Enter') void runQuery();
});

buildExampleButtons();
(examplesGrid.querySelector<HTMLButtonElement>('.ex-btn'))?.click();
